// Sending voice messages and files the way the app does (app/.../upload/MessageUploader.kt):
// a presigned PUT of the encrypted blob (on this origin, see media.ts), then POST /v1/messages.

import type { MediaUploadUrlResponse, MessageResponse, UploadUrlResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";
import type { Word } from "../audio/liveTranscript.ts";
import type { Recording } from "../audio/recorder.ts";
import type { VideoResult } from "../audio/videoTrack.ts";
import { ContentKind, encrypt, generateKey } from "../crypto/contentCipher.ts";
import { encryptMedia } from "../crypto/mediaCipher.ts";
import { encodeUtf8, toBase64 } from "./bytes.ts";
import { sameOrigin } from "./media.ts";

/** The server's limits (MessageService): pictures 25 MB, videos and files 100 MB. */
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
export const MAX_MEDIA_BYTES = 100 * 1024 * 1024;
const THUMB_EDGE = 720;
const MAX_THUMB_BYTES = 512 * 1024;

export interface Sent {
  response: MessageResponse;
  mck: Uint8Array<ArrayBuffer>;
  clientId?: string;
}

async function put(url: string, contentType: string, body: Uint8Array<ArrayBuffer>) {
  const response = await fetch(sameOrigin(url), { method: "PUT", headers: { "content-type": contentType }, body });
  if (!response.ok) throw new Error(`upload ${response.status}`);
}

/** [words]: the complete live transcript, if there is one; the server then need not transcribe. */
export async function sendVoice(session: Session, conversationId: string, recording: Recording, words?: Word[] | null): Promise<Sent> {
  const clientId = crypto.randomUUID();
  const mck = generateKey();
  const target = await session.call<UploadUrlResponse>("/v1/messages/upload-url", { method: "POST", json: { conversation_id: conversationId, client_id: clientId } });
  await put(target.upload_url, target.content_type, await encrypt(ContentKind.AUDIO, recording.ogg, mck));
  const response = await session.call<MessageResponse>("/v1/messages", {
    method: "POST",
    json: {
      client_id: clientId,
      kind: "voice",
      conversation_id: conversationId,
      audio_key: target.audio_key,
      duration_ms: recording.durationMs,
      waveform: toBase64(await encrypt(ContentKind.WAVEFORM, recording.waveform, mck)),
      mck: toBase64(mck),
      transcript: words?.length ? toBase64(await encrypt(ContentKind.TRANSCRIPT, encodeUtf8(JSON.stringify({ language: null, words })), mck)) : undefined,
    },
  });
  return { response, mck, clientId };
}

/**
 * The silent video track of a voice message just sent (docs/videonachricht-konzept.md): thumbnail
 * and video to the media keys of the message's client id, then PUT /v1/messages/{id}/video.
 */
export async function attachVideo(session: Session, conversationId: string, sent: Sent, video: VideoResult): Promise<void> {
  const clientId = sent.clientId!;
  let thumbKey: string | undefined;
  if (video.thumbnail) {
    const target = await session.call<MediaUploadUrlResponse>("/v1/media/upload-url", { method: "POST", json: { conversation_id: conversationId, client_id: clientId, thumbnail: true } });
    await put(target.upload_url, target.content_type, await encrypt(ContentKind.THUMB, video.thumbnail, sent.mck));
    thumbKey = target.key;
  }
  const plain = new Uint8Array(await video.blob.arrayBuffer());
  const target = await session.call<MediaUploadUrlResponse>("/v1/media/upload-url", { method: "POST", json: { conversation_id: conversationId, client_id: clientId, thumbnail: false } });
  await put(target.upload_url, target.content_type, await encryptMedia(plain, sent.mck));
  await session.call(`/v1/messages/${sent.response.id}/video`, {
    method: "PUT",
    json: { media_key: target.key, thumb_key: thumbKey, media: { bytes: plain.length, mime: video.mime, width: video.width, height: video.height }, segments: video.segments },
  });
}

export type MediaKind = "image" | "video" | "file";

export function kindOf(file: File): MediaKind {
  if (/^image\/(jpeg|png|webp|gif|avif)$/.test(file.type) && file.size <= MAX_IMAGE_BYTES) return "image";
  if (/^video\//.test(file.type)) return "video";
  return "file";
}

/** [extra]: more fields for the commit, e.g. board_id for a picture on a canvas. */
export async function sendFile(session: Session, conversationId: string, file: File, caption?: string, extra: Record<string, unknown> = {}): Promise<Sent> {
  const kind = kindOf(file);
  if (file.size > (kind === "image" ? MAX_IMAGE_BYTES : MAX_MEDIA_BYTES)) throw new Error("too large");
  const clientId = crypto.randomUUID();
  const mck = generateKey();
  const plain = new Uint8Array(await file.arrayBuffer());
  const picture = kind === "image" ? await thumbnail(file) : kind === "video" ? await videoThumbnail(file) : undefined;

  let thumbKey: string | undefined;
  if (picture) {
    const target = await session.call<MediaUploadUrlResponse>("/v1/media/upload-url", { method: "POST", json: { conversation_id: conversationId, client_id: clientId, thumbnail: true } });
    await put(target.upload_url, target.content_type, await encrypt(ContentKind.THUMB, picture.jpeg, mck));
    thumbKey = target.key;
  }
  const target = await session.call<MediaUploadUrlResponse>("/v1/media/upload-url", { method: "POST", json: { conversation_id: conversationId, client_id: clientId, thumbnail: false } });
  await put(target.upload_url, target.content_type, kind === "image" ? await encrypt(ContentKind.IMAGE, plain, mck) : await encryptMedia(plain, mck));

  const text = caption?.trim();
  const response = await session.call<MessageResponse>("/v1/messages", {
    method: "POST",
    json: {
      client_id: clientId,
      kind,
      conversation_id: conversationId,
      media_key: target.key,
      thumb_key: thumbKey,
      file_name: kind === "file" ? toBase64(await encrypt(ContentKind.FILE_NAME, encodeUtf8(file.name), mck)) : undefined,
      media: { bytes: file.size, mime: file.type || "application/octet-stream", width: picture?.width, height: picture?.height, duration_ms: picture?.durationMs ?? 0 },
      body: text ? toBase64(await encrypt(ContentKind.TEXT, encodeUtf8(text), mck)) : undefined,
      mck: toBase64(mck),
      ...extra,
    },
  });
  return { response, mck };
}

interface Picture {
  jpeg: Uint8Array<ArrayBuffer>;
  width: number;
  height: number;
  durationMs?: number;
}

/** A JPEG of at most 720 px on its long edge, like the app's thumbnails; the original's size. */
async function thumbnail(file: Blob): Promise<Picture | undefined> {
  try {
    const bitmap = await createImageBitmap(file);
    const result = await toJpeg(bitmap, bitmap.width, bitmap.height);
    bitmap.close();
    return result;
  } catch {
    return undefined;
  }
}

async function videoThumbnail(file: File): Promise<Picture | undefined> {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.muted = true;
    video.preload = "auto";
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("video"));
    });
    video.currentTime = Math.min(1, video.duration / 2 || 0);
    await new Promise((r) => (video.onseeked = r));
    const picture = await toJpeg(video, video.videoWidth, video.videoHeight);
    return picture && { ...picture, durationMs: Math.round(video.duration * 1000) };
  } catch {
    return undefined;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function toJpeg(source: CanvasImageSource, width: number, height: number): Promise<Picture | undefined> {
  if (!width || !height) return undefined;
  const scale = Math.min(1, THUMB_EDGE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d")!.drawImage(source, 0, 0, canvas.width, canvas.height);
  for (const quality of [0.82, 0.7, 0.55]) {
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", quality));
    if (blob && blob.size <= MAX_THUMB_BYTES) return { jpeg: new Uint8Array(await blob.arrayBuffer()), width, height };
  }
  return undefined;
}
